import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {Button} from 'reactstrap';
import ButtonGroup from '@mui/material/ButtonGroup';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import MuiTable from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { Translate, TextFormat, getPaginationState, JhiPagination, JhiItemCount } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { APP_DATE_FORMAT, APP_LOCAL_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './passenger.reducer';

export const Passenger = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const passengerList = useAppSelector(state => state.passenger.entities);
  const loading = useAppSelector(state => state.passenger.loading);
  const totalItems = useAppSelector(state => state.passenger.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    } else {
      return order === ASC ? faSortUp : faSortDown;
    }
  };

  return (
    <div>
      <h2 id="passenger-heading" data-cy="PassengerHeading">
        <Translate contentKey="jhipsterSampleApplicationApp.passenger.home.title">Passengers</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" onClick={handleSyncList} disabled={loading} variant="outlined" startIcon={<FontAwesomeIcon icon="sync" spin={loading} />}>
            <Translate contentKey="jhipsterSampleApplicationApp.passenger.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Button component={Link} to="/passenger/new" variant="contained" color="primary" id="jh-create-entity" data-cy="entityCreateButton" startIcon={<FontAwesomeIcon icon="plus" />}>
            <Translate contentKey="jhipsterSampleApplicationApp.passenger.home.createLabel">Create new Passenger</Translate>
          </Button>
        </div>
      </h2>
      <div className="table-responsive">
        {passengerList && passengerList.length > 0 ? (
          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            <MuiTable>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('id')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.passenger.id">ID</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('name')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.passenger.name">Name</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('name')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('email')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.passenger.email">Email</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('email')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('mobile')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.passenger.mobile">Mobile</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('mobile')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('age')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.passenger.age">Age</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('age')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('gender')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.passenger.gender">Gender</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('gender')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('nationality')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.passenger.nationality">Nationality</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('nationality')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('createdDate')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.passenger.createdDate">Created Date</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('createdDate')} />
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {passengerList.map((passenger, i) => (
                  <TableRow key={`entity-${i}`} data-cy="entityTable">
                    <TableCell>
                      <Button component={Link} to={`/passenger/${passenger.id}`} variant="text" size="small">
                        {passenger.id}
                      </Button>
                    </TableCell>
                    <TableCell>{passenger.name}</TableCell>
                    <TableCell>{passenger.email}</TableCell>
                    <TableCell>{passenger.mobile}</TableCell>
                    <TableCell>{passenger.age}</TableCell>
                    <TableCell>{passenger.gender}</TableCell>
                    <TableCell>{passenger.nationality}</TableCell>
                    <TableCell>
                      {passenger.createdDate ? <TextFormat type="date" value={passenger.createdDate} format={APP_DATE_FORMAT} /> : null}
                    </TableCell>
                    <TableCell className="text-end">
                      <ButtonGroup variant="outlined" size="small" aria-label="entity actions">
                        <Button component={Link} to={`/passenger/${passenger.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`} color="primary" startIcon={<FontAwesomeIcon icon="pencil-alt" />} data-cy="entityEditButton">
                        </Button>
                        <Button onClick={() => (window.location.href = `/passenger/${passenger.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)} color="error" startIcon={<FontAwesomeIcon icon="trash" />} data-cy="entityDeleteButton">
                        </Button>
                      </ButtonGroup>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </MuiTable>
          </TableContainer>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="jhipsterSampleApplicationApp.passenger.home.notFound">No Passengers found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={passengerList && passengerList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default Passenger;
